import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import { SG_AGM_EXEMPTED_AFS_TASK_CODE } from '../../constants/sgAgmTaskCodes';

export const agmExemptedAfsFlow: ChatFlow = {
  id: 'sg-agm-exempted-afs',
  taskCodeMatch: [SG_AGM_EXEMPTED_AFS_TASK_CODE],
  categoryMatch: [
    'agm exempted with audited financial statements',
    'agm exempted audited',
    'exempted agm audited',
  ],

  getInitialMessages(preSet): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}** (AGM Exempted with Audited Financial Statements).\n\n` +
          (docCount > 0 ? `This preset includes ${docCount} document(s).\n\n` : '') +
          `First, enter a **Task Title**. Then choose the **company entity** to continue.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage:
        'Search and select the **Singapore company** for which the AGM documents will be prepared.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select entity (AGM Exempted with Audited Financial Statements).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. On the right, fill in the AGM details and click **Create Task** when ready.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'officer-role',
      aiMessage: 'Select an officer role.',
      widget: 'officer-role-buttons',
      widgetConfig: {
        targetVarKey: '_officerRole',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the entity.',
      widget: 'officer-list',
      widgetConfig: { targetVarKey: '_officer', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person selected.',
      widget: null,
      nextStep: 'confirm',
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityNameVal = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${entityNameVal}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          entityName: entityNameVal,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [
            {
              role: 'ai',
              content: 'Please select an entity first.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }
      const fg = String(ctx.fieldGroup || 'person');
      if (ctx.defaultRole) {
        return {
          messages: [
            {
              role: 'user',
              content: `Looking up **${fg}** (role: ${ctx.defaultRole})...`,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'officer-list',
          nextStepWidgetData: {
            entityCd,
            selectedRole: ctx.defaultRole,
            ...(ctx.memberEntityKind === 'corporate' || ctx.memberEntityKind === 'individual'
              ? { memberEntityKind: ctx.memberEntityKind }
              : {}),
          },
        };
      }
      return {
        messages: [
          {
            role: 'user',
            content: `Looking up **${fg}**...`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-role',
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [
          {
            role: 'user',
            content: `Role: **${role}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          entityCd,
          selectedRole: role,
        },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected person: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    return {};
  },
};
